import { createFileRoute, redirect } from '@tanstack/react-router'

const SUPPORTED_LOCALES = ['es', 'gl', 'en', 'de', 'fr', 'it', 'nl', 'pt']
const DEFAULT_LOCALE = 'en' // B-new-1008 — unified default locale (was 'es')

/**
 * Catch-all splat route — handles paths that are missing the /$locale prefix.
 * e.g. /sign-in            → /en/sign-in
 *      /dashboard/overview → /en/dashboard/overview
 *      /job-tracker        → /en/job-tracker
 *
 * **Critical guard (added 2026-05-29 after a production redirect-loop bug):**
 *
 * If the splat ALREADY starts with a supported locale (e.g. visitor lands on
 * /es/search and there's no `/$locale/search` route file), the old splat
 * would naively redirect to `/es/es/search`. That target also has no
 * matching route → falls back to this splat again → redirects to
 * `/es/es/es/search`. Browser URL grows exponentially per hop until
 * Cloudflare or the browser caps the URL length. Customer impact: any
 * locale-prefixed URL whose tail doesn't match a real route becomes an
 * infinite loop instead of a clean 404 / home redirect.
 *
 * Fix: when the splat's first segment is already a supported locale,
 * redirect to that locale's HOME page (a guaranteed-existing route) rather
 * than prepending the default locale again. One redirect lands the user
 * on the home page in their own language instead of a 50-deep recursion.
 */
export const Route = createFileRoute('/$')({
  beforeLoad: ({ params }) => {
    const splat = ((params as Record<string, unknown>)._splat as string | undefined) ?? ''

    // Detect "already locale-prefixed but no child route matched" — the
    // case that produced the production /es/es/.../search loop.
    const firstSegment = splat.split('/')[0]
    if (SUPPORTED_LOCALES.includes(firstSegment)) {
      // We could 404 here instead, but redirecting to the locale's home
      // keeps the user on a working page in their own language. The home
      // route is guaranteed to exist at /$locale/index.tsx.
      throw redirect({ href: `/${firstSegment}` })
    }

    // Normal case: bare path with no locale → prepend the default.
    throw redirect({
      href: `/${DEFAULT_LOCALE}/${splat}`,
    })
  },
  component: () => null,
})
